/**
 * Pure-function builder for the web Notification API's `icon` URL and `title`.
 *
 * Web analogue of `CommunicationIntentBuilder` on iOS. Where iOS uses
 * `INSendMessageIntent` + Sirikit to achieve rich avatar banners, the web has
 * only the `Notification` API's `icon` and `title` fields. The privacy
 * invariants from the iOS threat model transfer verbatim; the Sirikit-specific
 * rules (handle type, suggestion type, donation) have no web equivalent and are
 * documented in `kaether_for_web/docs/rich-notification.md`.
 *
 * Threat model (mirrors CommunicationIntentBuilder.swift):
 *
 *   1. Non-roster senders surface as title="Unknown" with no icon. No
 *      peer-claimed identity ever reaches the Notification banner for a peer
 *      we don't trust.
 *   2. Avatar bytes > 200 KB are dropped and `icon` is omitted. The cap blocks
 *      decompression-bomb attempts and bounds memory pressure during Service
 *      Worker notification delivery.
 *   3. Privacy-mode (`hideOnLockScreen: true`) short-circuits the entire
 *      rich-notification path: the builder returns { title: "Unknown",
 *      body: "New message" } with no icon, so the push payload leaks nothing
 *      about sender identity even if the banner is visible on the lock screen.
 *   4. There is no field on `RichNotificationInputs` that accepts a
 *      peer-claimed or server-supplied display name. The `senderProfile`
 *      parameter is deliberately absent from the inputs struct; the self-test
 *      asserts this by passing a shape with injected peer-claimed fields and
 *      verifying the title is still "Unknown" / trustedDisplayName.
 *
 * This module is pure-logic. It never calls `new Notification(...)`, never
 * fetches a URL, and imports no Solid UI primitives. Avatar bytes are passed
 * in as a `Uint8Array | null` from the local avatar cache; the caller is
 * responsible for the cache read. The builder converts bytes to a `data:` URL
 * so Service Workers (which cannot call `URL.createObjectURL`) can still use
 * the icon.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Hard cap on avatar bytes passed to the Notification icon. Mirrors
 * `CommunicationIntentBuilder.maxAvatarBytes` on iOS.
 */
export const MAX_AVATAR_BYTES = 200_000;

/** Literal string used for any non-roster or unresolvable sender. */
export const UNKNOWN_DISPLAY_NAME = "Unknown";

/** Body used for all privacy-mode notifications. */
export const PRIVACY_MODE_BODY = "New message";

// ---------------------------------------------------------------------------
// Input / output types
// ---------------------------------------------------------------------------

/**
 * All locally-trusted inputs the builder needs. Mirrors `CommunicationIntentInputs`
 * on iOS minus fields that have no web equivalent (recipients, groupName, etc.).
 *
 * The deliberate absence of any `peerClaimedName`, `senderClaimedName`, or
 * similar field means peer/server strings have no path into the Notification
 * title. The self-test asserts this by passing an object with injected fields
 * and verifying output is unaffected.
 */
export interface RichNotificationInputs {
  /** Sender's stable userId — used as a diagnostic handle, never displayed. */
  readonly senderUserId: string;
  /**
   * Display name from our local roster. Undefined if we have no
   * human-readable name pinned for this peer.
   */
  readonly trustedDisplayName?: string;
  /** Whether the sender is in our roster. */
  readonly isSenderInRoster: boolean;
  /**
   * Avatar bytes from the LOCAL avatar cache. Null for "no cached avatar."
   * Must be a complete image (PNG/JPEG); the builder does not fetch,
   * decompress, or validate the format beyond the byte-count cap.
   */
  readonly avatarBytes: Uint8Array | null;
  /**
   * When true, the user has enabled privacy mode / hidden previews. The
   * builder returns a constant "Unknown" / "New message" payload with no icon
   * so the banner leaks nothing about sender identity.
   */
  readonly hideOnLockScreen: boolean;
}

/** Shape returned by the builder. Only `icon` is optional. */
export interface RichNotification {
  /** Notification title — always from the local roster; never peer-supplied. */
  readonly title: string;
  /** Notification body text. */
  readonly body: string;
  /**
   * `data:` URL encoding the avatar bytes. Absent when:
   *   - avatarBytes is null or zero-length
   *   - avatarBytes.length > MAX_AVATAR_BYTES
   *   - sender is not in roster
   *   - trustedDisplayName is empty/absent (no face next to "Unknown")
   *   - hideOnLockScreen is true
   */
  readonly icon?: string;
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * Build a `RichNotification` from locally-trusted inputs.
 *
 * The function is pure: same inputs always produce the same output. It does
 * not call `Date.now()`, `Math.random()`, or any I/O API.
 *
 * @param inputs - Locally-trusted notification inputs; see {@link RichNotificationInputs}.
 * @param body   - The notification body text (already prepared by the caller
 *                 from the decrypted message, or a generic placeholder). The
 *                 builder does not inspect body content.
 */
export function buildRichNotification(
  inputs: RichNotificationInputs,
  body: string,
): RichNotification {
  // Privacy-mode short-circuit: return constant payload with no icon.
  if (inputs.hideOnLockScreen) {
    return { title: UNKNOWN_DISPLAY_NAME, body: PRIVACY_MODE_BODY };
  }

  const title = resolveTitle(inputs);
  const icon = resolveIcon(inputs, title);

  return icon !== undefined ? { title, body, icon } : { title, body };
}

// ---------------------------------------------------------------------------
// Internal helpers (exported for tests)
// ---------------------------------------------------------------------------

/**
 * Resolve the notification title from locally-trusted roster data.
 *
 * Visible for tests. Applies the redaction rule:
 *   - In-roster + non-empty trustedDisplayName → trustedDisplayName
 *   - Anything else (not in roster, nil name, empty name) → "Unknown"
 *
 * Peer-claimed name has no path here: `RichNotificationInputs` has no such
 * field, and the self-test exercises a superset shape to confirm that.
 */
export function resolveTitle(inputs: Pick<RichNotificationInputs, "isSenderInRoster" | "trustedDisplayName">): string {
  const hasName =
    inputs.isSenderInRoster &&
    typeof inputs.trustedDisplayName === "string" &&
    inputs.trustedDisplayName.trim().length > 0;

  return hasName ? inputs.trustedDisplayName! : UNKNOWN_DISPLAY_NAME;
}

/**
 * Resolve the icon `data:` URL from avatar bytes.
 *
 * Visible for tests. Returns undefined when:
 *   - the resolved title is "Unknown" (we never show a face next to "Unknown")
 *   - avatarBytes is null or empty
 *   - avatarBytes.length > MAX_AVATAR_BYTES
 *
 * The bytes-to-data-URL conversion uses base64, which works in both browser
 * main-thread (via `btoa`) and Service Worker contexts.
 */
export function resolveIcon(
  inputs: Pick<RichNotificationInputs, "avatarBytes">,
  resolvedTitle: string,
): string | undefined {
  // Do not show a face next to "Unknown" — that pairing invites the user to
  // mentally fill in an identity for the avatar. Mirrors the iOS comment in
  // CommunicationIntentBuilder._redactPerson.
  if (resolvedTitle === UNKNOWN_DISPLAY_NAME) {
    return undefined;
  }

  const bytes = inputs.avatarBytes;

  if (bytes === null || bytes.length === 0) {
    return undefined;
  }

  if (bytes.length > MAX_AVATAR_BYTES) {
    return undefined;
  }

  return bytesToDataUrl(bytes);
}

/**
 * Convert raw bytes to a `data:` URL with MIME type `image/png`.
 *
 * Using `image/png` as the MIME type is a simplification acceptable for the
 * notification icon path: browsers accept PNG data URLs in `Notification.icon`
 * and the local avatar cache stores PNG-encoded images. If JPEG avatars are
 * added later, the caller can pass a `mimeType` parameter; for now the
 * default is sufficient and avoids format-sniffing logic in this builder.
 *
 * Pure function — no I/O.
 */
function bytesToDataUrl(bytes: Uint8Array, mimeType = "image/png"): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `data:${mimeType};base64,${btoa(binary)}`;
}
